import { useEffect, useMemo, useRef } from 'react';

import { displayScale } from './peaks';
import { playerElement } from './element';

/**
 * The room lights, on the beat.
 *
 * ## What it is
 *
 * Three soft coloured lamps behind everything, doing what the LEDs on a
 * Bluetooth speaker do: breathing with how loud the track is and kicking on the
 * beat. The colour drifts slowly so a long song does not sit under one wash.
 *
 * ## It is the music, not a timer
 *
 * The peaks file the transcoder already writes (§4.3) is 1800 true amplitudes
 * per track. Loudness comes straight off it; the beat comes from where that
 * loudness *jumps* - a value well above the last second of itself, which is
 * what a kick is. Nothing is random and nothing runs on a clock, which is the
 * whole difference between this and the light shows that came with media
 * players: those hit the beat by coincidence and you could always tell.
 *
 * ## Why not a real analyser
 *
 * `AnalyserNode` would give a spectrum, and a spectrum is a better beat
 * detector than an envelope. It needs `createMediaElementSource`, which needs
 * CORS-clean audio; ours is HLS behind a signed cookie through a worker (§4.4),
 * so the honest outcome of trying is a light rig that never lights for anybody.
 * The peaks are already on disk, already correct, and already paid for.
 *
 * ## Light, not paint
 *
 * `screen` blending, so the lamps add to what is behind them rather than
 * covering it - which is the difference between a light in a dark room and a
 * coloured sheet over the screen. Nothing here is ever bright enough to make
 * the words in front of it harder to read; the moment it is, it has stopped
 * being a background.
 */

/** Where the lamps hang, how big they are, and how hard each one takes a hit. */
const LAMPS = [
  { colour: '124,132,242', x: '18%', y: '28%', size: '78vmax', weight: 1 },
  { colour: '231,84,166', x: '84%', y: '22%', size: '66vmax', weight: 0.78 },
  { colour: '63,191,191', x: '52%', y: '92%', size: '72vmax', weight: 0.62 },
] as const;

/**
 * How much of the recent past a beat has to beat.
 *
 * Nine points is about a second at the resolution the peaks file has, which is
 * long enough that a kick stands out against the bar it is in and short enough
 * that a chorus arriving does not read as one enormous beat.
 */
const HISTORY = 9;

/** Per frame. Roughly a third of a second from full to nothing at 60fps. */
const DECAY = 0.9;

export function Lights({
  peaks,
  duration,
  playing,
  position,
  /**
   * Something to glow with the lamps, usually the artwork.
   *
   * A ref rather than a rendered prop because this runs sixty times a second:
   * pushing the level through React state would re-render the whole screen on
   * every frame to move one blurred image.
   */
  glow,
}: {
  peaks: number[] | null;
  /** Seconds. Without it there is no way to map a moment to a peak. */
  duration: number;
  playing: boolean;
  /** The store's position, for before the audio element is reachable. */
  position: number;
  glow?: React.RefObject<HTMLElement | null>;
}) {
  const lamps = useRef<(HTMLDivElement | null)[]>([]);

  /** How loud, smoothed. Quick to rise, slow to fall - like a light does. */
  const level = useRef(0);
  /** How recently the beat hit, decaying. */
  const hit = useRef(0);

  /*
   * Onset strength, worked out once per track rather than per frame.
   *
   * A beat is not "loud", it is "louder than it just was" - a quiet track has
   * beats and a wall of distortion mostly does not. So each point is measured
   * against the second before it and the whole curve is normalised, which is
   * also what keeps a quiet recording lighting up as much as a squashed one.
   */
  const onsets = useMemo(() => strength(peaks), [peaks]);

  useEffect(() => {
    if (peaks === null || peaks.length === 0 || duration <= 0) return;

    const scale = displayScale(peaks);

    /*
     * Somebody who has asked not to be moved is not moved.
     *
     * A steady wash rather than nothing: the colour is atmosphere and worth
     * keeping, and only the flashing was ever the problem. Flashing is also the
     * exact thing this setting exists to stop.
     */
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let frame = 0;
    let started = 0;

    const draw = (now: number) => {
      if (started === 0) started = now;

      const at = playerElement()?.currentTime ?? position;
      const played = Math.min(1, Math.max(0, at / duration));
      const index = played * (peaks.length - 1);

      const loud = Math.min(1, sample(peaks, index) * scale);
      const beat = sample(onsets, index);

      // Attack fast, release slow. A symmetrical slide makes every transient
      // look like a swell, which is the opposite of what a beat is.
      level.current += (loud - level.current) * (loud > level.current ? 0.3 : 0.06);
      hit.current = Math.max(hit.current * DECAY, beat);

      const lit = still ? 0.35 : level.current;
      const kick = still ? 0 : hit.current;

      /*
       * A slow drift, so a six-minute track is not six minutes of one colour.
       * Off the clock rather than off the music: this is the room, not the song.
       *
       * Set on each lamp rather than on the rig around them, and that is not a
       * detail. A filter makes a stacking context, and a stacking context
       * isolates its children's blending - so one `hue-rotate` on the container
       * would quietly turn every `mix-blend-screen` below it into paint over a
       * transparent backdrop instead of light over the room.
       */
      const drift = still ? 0 : ((now - started) / 1000) * 5;

      for (let i = 0; i < LAMPS.length; i++) {
        const lamp = lamps.current[i];

        if (!lamp) continue;

        const weight = LAMPS[i]?.weight ?? 1;

        /*
         * Each lamp takes the same beat by a different amount.
         *
         * Three lights rising and falling in perfect unison read as one light
         * that happens to be three colours. Weighting them differently is what
         * makes it a rig.
         */
        lamp.style.opacity = (0.1 + lit * 0.22 + kick * 0.4 * weight).toFixed(3);
        lamp.style.transform = `scale(${(0.94 + lit * 0.06 + kick * 0.16 * weight).toFixed(3)})`;
        lamp.style.filter = `hue-rotate(${drift.toFixed(1)}deg)`;
      }

      if (glow?.current) {
        glow.current.style.opacity = (0.18 + lit * 0.3 + kick * 0.25).toFixed(3);
        glow.current.style.transform = `scale(${(1.03 + lit * 0.08 + kick * 0.09).toFixed(3)})`;
      }

      if (playing && !still) frame = requestAnimationFrame(draw);
    };

    draw(performance.now());

    return () => cancelAnimationFrame(frame);
  }, [peaks, onsets, duration, playing, position, glow]);

  // No peaks is no light show, and nothing rendered rather than three lamps
  // sitting at a fixed brightness pretending to be one.
  if (peaks === null || peaks.length === 0 || duration <= 0) return null;

  return (
    <div
      aria-hidden
      data-lights
      className="pointer-events-none fixed inset-0 overflow-hidden"
    >
      {LAMPS.map((lamp, i) => (
        <div
          key={lamp.colour}
          ref={(node) => {
            lamps.current[i] = node;
          }}
          className="absolute mix-blend-screen will-change-transform"
          style={{
            left: lamp.x,
            top: lamp.y,
            width: lamp.size,
            height: lamp.size,
            marginLeft: `calc(${lamp.size} / -2)`,
            marginTop: `calc(${lamp.size} / -2)`,
            // The gradient's own falloff, rather than a CSS blur. A blur this
            // size is a full-screen filter recomputed whenever the element
            // moves, and the shape wanted here is exactly what a radial
            // gradient already is.
            background: `radial-gradient(closest-side, rgba(${lamp.colour},0.85), rgba(${lamp.colour},0) 72%)`,
            opacity: 0.12,
          }}
        />
      ))}
    </div>
  );
}

/**
 * How much of a beat each point is: how far it rises above the second before it.
 *
 * Normalised across the track, so the lights respond to a quiet recording as
 * readily as to a loud one - the same argument as `displayScale`, for the same
 * reason. A track with no dynamics at all comes back flat, which is correct:
 * there is nothing there to flash on.
 */
function strength(peaks: number[] | null): number[] {
  if (peaks === null || peaks.length === 0) return [];

  const out: number[] = Array.from({ length: peaks.length }, () => 0);
  let loudest = 0;

  for (let i = 0; i < peaks.length; i++) {
    let total = 0;
    let counted = 0;

    for (let j = Math.max(0, i - HISTORY); j < i; j++) {
      total += peaks[j] ?? 0;
      counted += 1;
    }

    const rise = Math.max(0, (peaks[i] ?? 0) - (counted > 0 ? total / counted : 0));

    out[i] = rise;
    loudest = Math.max(loudest, rise);
  }

  if (loudest > 0) {
    for (let i = 0; i < out.length; i++) out[i] = (out[i] ?? 0) / loudest;
  }

  return out;
}

/**
 * An array read at a fractional index.
 *
 * Interpolated rather than rounded: the resolution is fixed at 1800 points
 * however long the track is, so rounding makes a three-minute song step nine
 * times a second and an hour-long mix step twice a minute. Reading between the
 * points means both are smooth, and nothing is invented - a value between two
 * measurements is still bounded by them.
 */
function sample(values: number[], at: number): number {
  if (values.length === 0 || at < 0) return 0;

  const low = Math.min(values.length - 1, Math.floor(at));
  const high = Math.min(values.length - 1, low + 1);
  const mix = at - low;

  return (values[low] ?? 0) * (1 - mix) + (values[high] ?? 0) * mix;
}
